import { Translation } from '@ps/web-shared-components';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import withWidgetTestId from '@swf/core/hoc/withWidgetTestId';
import type { WidgetFC } from '@swf/core/types/Widget';
import { Message, TryAgainMessage } from '@swf/sports/components';
import GroupedTemplatePanels from '@swf/sports/components/GroupedTemplatePanels/GroupedTemplatePanels';
import LiveDataWarning from '@swf/sports/components/LiveDataWarning/LiveDataWarning';
import useLiveDataPolling from '@swf/sports/hooks/useLiveDataPolling/useLiveDataPolling';
import useMarketUpdates from '@swf/sports/hooks/useMarketUpdates';
import { getEventTypesInPlayEventIdsMap } from '@swf/sports/utils/getEventTypesInPlayEventIdsMap';
import getEventsFromMarketTemplateData from '@swf/sports/utils/getEventsFromMarketTemplateData';

import { TXTWCL_There_are_no_active_in_play_ev_ELL } from './SportsSportInPlayEventsWidget.messages.json';
import GroupedTemplatePanelsLoadingState from './components/LoadingState/LoadingState';
import { sportsSportInPlayEventsWidgetModel, useSportsSportInPlayEventsWidget } from './model';

const SportsSportInPlayEventsWidget: WidgetFC = () => {
  const { widgetDispatch, widgetSelect } = useSportsSportInPlayEventsWidget();

  const events = useSelector(widgetSelect.events);
  const markets = useSelector(widgetSelect.markets);
  const groups = useSelector(widgetSelect.groups);
  const competitions = useSelector(widgetSelect.competitions);
  const eventTypes = useSelector(widgetSelect.eventTypes);
  const marketIds = useSelector(widgetSelect.marketIds);
  const error = useSelector(widgetSelect.error);
  const isLoading = useSelector(widgetSelect.isLoading);

  const eventTypesEventIdsMap = useMemo(
    () =>
      getEventTypesInPlayEventIdsMap(
        getEventsFromMarketTemplateData(events, markets, competitions, eventTypes)
      ),
    [competitions, eventTypes, events, markets]
  );

  useLiveDataPolling({ eventTypesEventIdsMap });

  useMarketUpdates(marketIds, widgetDispatch.marketsUpdated);
  if (isLoading && !groups.length) {
    return <GroupedTemplatePanelsLoadingState />;
  }

  if (error === 404) {
    return <Message message={<Translation {...TXTWCL_There_are_no_active_in_play_ev_ELL} />} />;
  }

  if (typeof error !== 'undefined') {
    return <TryAgainMessage />;
  }

  if (!groups.length) {
    return <Message message={<Translation {...TXTWCL_There_are_no_active_in_play_ev_ELL} />} />;
  }

  return (
    <>
      <GroupedTemplatePanels groupedOn="COMPETITION" groups={groups} />
      <LiveDataWarning />
    </>
  );
};

SportsSportInPlayEventsWidget.model = sportsSportInPlayEventsWidgetModel;

export default withWidgetTestId(SportsSportInPlayEventsWidget, 'sports-sport-in-play-events');
